/**
 * ToastProvider — global toast notification system.
 *
 * Wraps the app to provide `useToast()` hook for success/error/warning/info
 * notifications from any component without local Snackbar state.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved!');
 *   toast.error('Something went wrong');
 *   toast.warning('Battery low');
 *   toast.info('Sync in progress');
 */

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Snackbar, type AlertColor } from '@mui/material';

interface ToastMessage {
  id: number;
  severity: AlertColor;
  message: string;
  duration: number;
}

interface ToastContextValue {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback(
    (severity: AlertColor, message: string, duration = 4000) => {
      const id = ++nextId;
      setToasts((prev) => [...prev, { id, severity, message, duration }]);
    },
    [],
  );

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value: ToastContextValue = {
    success: useCallback(
      (msg, dur?) => addToast('success', msg, dur),
      [addToast],
    ),
    error: useCallback(
      (msg, dur?) => addToast('error', msg, dur ?? 6000),
      [addToast],
    ),
    warning: useCallback(
      (msg, dur?) => addToast('warning', msg, dur),
      [addToast],
    ),
    info: useCallback((msg, dur?) => addToast('info', msg, dur), [addToast]),
  };

  // Show the first toast in the queue (stacked display)
  const current = toasts[0];

  return (
    <ToastContext.Provider value={value}>
      {children}
      {current && (
        <Snackbar
          key={current.id}
          open
          autoHideDuration={current.duration}
          onClose={() => removeToast(current.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => removeToast(current.id)}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%', minWidth: 280 }}
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>');
  }
  return ctx;
}
